import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '../api.js';
import { Plus, Trash2, FolderPlus, Folder } from 'lucide-react';
import { useT } from '../i18n/useT.js';
import { useAlert } from '../components/AlertProvider.jsx';

export default function TemplatesPage() {
  const t = useT();
  const { showAlert, showConfirm } = useAlert();
  const [templates, setTemplates] = useState([]);
  const [folders, setFolders] = useState([]);
  const [selectedFolderId, setSelectedFolderId] = useState('');
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [showCreateFolder, setShowCreateFolder] = useState(false);
  const [newName, setNewName] = useState('');
  const [newFolderName, setNewFolderName] = useState('');
  const navigate = useNavigate();

  const load = async () => {
    setLoading(true);
    try {
      const params = selectedFolderId ? { folderId: selectedFolderId } : {};
      const res = await api.getTemplates(params);
      setTemplates(res.items || []);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const loadFolders = async () => {
    try {
      const res = await api.getFolders();
      setFolders(res.items || []);
    } catch (err) {
      console.error(err);
    }
  };

  useEffect(() => {
    loadFolders();
  }, []);

  useEffect(() => {
    load();
  }, [selectedFolderId]);

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!newName.trim()) return;
    try {
      const tpl = await api.createTemplate({
        name: newName,
        engine: 'handlebars',
        recipe: 'chrome-pdf',
        contentHtml: '<html><body><h1>Hello {{name}}</h1></body></html>',
        folderId: selectedFolderId || undefined,
      });
      setShowCreate(false);
      setNewName('');
      navigate(`/templates/${tpl.id}`);
    } catch (err) {
      showAlert(err.message, { tone: 'error', title: t('billing.loadFailed', 'Failed') });
    }
  };

  const handleDelete = async (id) => {
    const accepted = await showConfirm(t('templates.confirms.deleteTemplate', 'Delete this template?'), {
      title: t('templates.deleteFolderTitle', 'Delete'),
      confirmLabel: t('billing.yes', 'Yes'),
      cancelLabel: t('templates.modal.cancel', 'Cancel'),
      tone: 'warning',
    });
    if (!accepted) return;
    try {
      await api.deleteTemplate(id);
      setTemplates((prev) => prev.filter((t) => t.id !== id));
    } catch (err) {
      showAlert(err.message || t('templates.errors.deleteTemplate', 'Failed to delete template'), { tone: 'error', title: t('billing.loadFailed', 'Failed') });
    }
  };

  const handleCreateFolder = async (e) => {
    e.preventDefault();
    if (!newFolderName.trim()) return;
    try {
      const folder = await api.createFolder({ name: newFolderName.trim() });
      setFolders((prev) => [...prev, folder].sort((a, b) => (a.path || '').localeCompare(b.path || '')));
      setSelectedFolderId(folder.id);
      setShowCreateFolder(false);
      setNewFolderName('');
      await load();
    } catch (err) {
      showAlert(err.message || t('templates.errors.createFolder', 'Failed to create folder'), { tone: 'error', title: t('billing.loadFailed', 'Failed') });
    }
  };

  const handleDeleteFolder = async (folderId, folderName) => {
    const accepted = await showConfirm(t('templates.confirms.deleteFolder', 'Delete folder "{{name}}"? Templates will be unassigned from this folder.', { name: folderName }), {
      title: t('templates.deleteFolderTitle', 'Delete folder'),
      confirmLabel: t('billing.yes', 'Yes'),
      cancelLabel: t('templates.modal.cancel', 'Cancel'),
      tone: 'warning',
    });
    if (!accepted) return;
    try {
      await api.deleteFolder(folderId);
      setFolders((prev) => prev.filter((f) => f.id !== folderId));
      if (selectedFolderId === folderId) setSelectedFolderId('');
      await load();
    } catch (err) {
      showAlert(err.message || t('templates.errors.deleteFolder', 'Failed to delete folder'), { tone: 'error', title: t('billing.loadFailed', 'Failed') });
    }
  };

  const getFolderLabel = (folderId) => {
    if (!folderId) return t('templates.uncategorized', 'No folder');
    return folders.find((f) => f.id === folderId)?.name || t('templates.folderFallback', 'Folder');
  };

  return (
    <>
      <div className="toolbar">
        <h2>{t('templates.title', 'Templates')}</h2>
        <div className="toolbar-actions">
          <button className="btn-secondary" onClick={() => setShowCreateFolder(true)}>
            <FolderPlus size={14} style={{ marginRight: 4 }} /> {t('templates.newFolder', 'New Folder')}
          </button>
          <button className="btn-primary" onClick={() => setShowCreate(true)}>
            <Plus size={14} style={{ marginRight: 4 }} /> {t('templates.newTemplate', 'New Template')}
          </button>
        </div>
      </div>

      <div className="page-content templates-layout">
        <div className="folders-panel card">
          <div className="folders-header">{t('templates.folders', 'Folders')}</div>
          <button
            className={`folder-item ${selectedFolderId === '' ? 'active' : ''}`}
            onClick={() => setSelectedFolderId('')}
          >
            <Folder size={14} style={{ marginRight: 6 }} /> {t('templates.allTemplates', 'All Templates')}
          </button>
          {folders
            .filter((f) => f.path !== '/')
            .map((folder) => (
              <div key={folder.id} className="folder-item-row">
                <button
                  className={`folder-item ${selectedFolderId === folder.id ? 'active' : ''}`}
                  onClick={() => setSelectedFolderId(folder.id)}
                >
                  <Folder size={14} style={{ marginRight: 6 }} /> {folder.path || folder.name}
                </button>
                <button
                  className="btn-secondary folder-delete-btn"
                  title={t('templates.deleteFolderTitle', 'Delete folder')}
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    handleDeleteFolder(folder.id, folder.name);
                  }}
                >
                  <Trash2 size={12} />
                </button>
              </div>
            ))}
        </div>

        <div className="templates-panel">
          {loading ? (
            <p>{t('templates.loading', 'Loading...')}</p>
          ) : templates.length === 0 ? (
            <div className="empty-state">
              <p>{t('templates.emptyFolder', 'No templates yet in this folder')}</p>
              <button className="btn-primary" style={{ marginTop: 12 }} onClick={() => setShowCreate(true)}>
                {t('templates.createTemplate', 'Create template')}
              </button>
            </div>
          ) : (
            <table className="table">
              <thead>
                <tr>
                  <th>{t('templates.table.name', 'Name')}</th>
                  <th>{t('templates.table.folder', 'Folder')}</th>
                  <th>{t('templates.table.engine', 'Engine')}</th>
                  <th>{t('templates.table.recipe', 'Recipe')}</th>
                  <th>{t('templates.table.updated', 'Updated')}</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {templates.map((t) => (
                  <tr
                    key={t.id}
                    style={{ cursor: 'pointer' }}
                    onClick={(e) => {
                      if (e.target.closest('button')) return;
                      navigate(`/templates/${t.id}`);
                    }}
                  >
                    <td>{t.name}</td>
                    <td>{getFolderLabel(t.folder_id)}</td>
                    <td><span className="badge badge-info">{t.engine}</span></td>
                    <td><span className="badge badge-success">{t.recipe}</span></td>
                    <td>{new Date(t.updated_at).toLocaleDateString()}</td>
                    <td>
                      <button
                        className="btn-secondary"
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          handleDelete(t.id);
                        }}
                      >
                        <Trash2 size={14} />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {showCreateFolder && (
        <div className="modal-overlay" onClick={() => setShowCreateFolder(false)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <h3>{t('templates.newFolder', 'New Folder')}</h3>
            <form onSubmit={handleCreateFolder}>
              <div className="form-group">
                <label>{t('templates.modal.folderName', 'Folder name')}</label>
                <input
                  value={newFolderName}
                  onChange={(e) => setNewFolderName(e.target.value)}
                  autoFocus
                  required
                />
              </div>
              <div className="modal-actions">
                <button type="button" className="btn-secondary" onClick={() => setShowCreateFolder(false)}>
                  {t('templates.modal.cancel', 'Cancel')}
                </button>
                <button type="submit" className="btn-primary">{t('templates.modal.create', 'Create')}</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {showCreate && (
        <div className="modal-overlay" onClick={() => setShowCreate(false)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <h3>{t('templates.newTemplate', 'New Template')}</h3>
            <form onSubmit={handleCreate}>
              <div className="form-group">
                <label>{t('templates.modal.name', 'Name')}</label>
                <input value={newName} onChange={(e) => setNewName(e.target.value)} autoFocus required />
              </div>
              <div className="form-group">
                <label>{t('templates.modal.folder', 'Folder')}</label>
                <select value={selectedFolderId} onChange={(e) => setSelectedFolderId(e.target.value)}>
                  <option value="">{t('templates.modal.noFolder', 'No folder')}</option>
                  {folders
                    .filter((f) => f.path !== '/')
                    .map((f) => (
                      <option key={f.id} value={f.id}>{f.path || f.name}</option>
                    ))}
                </select>
              </div>
              <div className="modal-actions">
                <button type="button" className="btn-secondary" onClick={() => setShowCreate(false)}>{t('templates.modal.cancel', 'Cancel')}</button>
                <button type="submit" className="btn-primary">{t('templates.modal.create', 'Create')}</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </>
  );
}
